import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { LogOut, Fingerprint, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isWebAuthnSupported, enrollBiometric } from "@/lib/webauthn";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [creds, setCreds] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/webauthn/credentials");
      setCreds(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const enroll = async () => {
    if (!isWebAuthnSupported()) {
      toast.error("Biométrie non supportée");
      return;
    }
    setEnrolling(true);
    try {
      const name = navigator.userAgent.match(/(iPhone|iPad|Android|Macintosh|Windows|Linux)/)?.[0] || "Cet appareil";
      await enrollBiometric(name);
      toast.success("Biométrie activée !");
      load();
    } catch (e) {
      toast.error(e.message || "Échec de l'enregistrement");
    } finally {
      setEnrolling(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet appareil ?")) return;
    await api.delete(`/webauthn/credentials/${id}`);
    load();
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Profil</h1>

      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-2xl font-semibold">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div className="font-semibold text-lg">{user?.name}</div>
            <div className="text-stone-500 text-sm">{user?.email}</div>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg">Biométrie</h3>
              <p className="text-sm text-stone-500 mt-0.5">Empreinte, Face ID ou clé de sécurité pour vous connecter sans mot de passe.</p>
            </div>
          </div>
          <Button onClick={enroll} disabled={enrolling}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800"
                  data-testid="btn-enroll-biometric">
            <Fingerprint className="w-4 h-4 mr-2" />
            {enrolling ? "Activation..." : "Ajouter un appareil"}
          </Button>
        </div>

        {creds.length > 0 && (
          <div className="mt-5 divide-y divide-stone-100 border-t border-stone-100">
            {creds.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-3" data-testid={`bio-cred-${c.id}`}>
                <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center">
                  <Fingerprint className="w-4 h-4 text-stone-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{c.device_name}</div>
                  <div className="text-xs text-stone-500">
                    Ajouté le {new Date(c.created_at).toLocaleDateString("fr-FR")}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)}
                        data-testid={`bio-cred-delete-${c.id}`}>
                  <Trash2 className="w-4 h-4 text-stone-400" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Button variant="outline" onClick={async () => { await logout(); navigate("/login"); }}
              className="rounded-xl" data-testid="profile-logout">
        <LogOut className="w-4 h-4 mr-2" /> Déconnexion
      </Button>
    </div>
  );
}
